import { CanonicalLoad } from '@numeo/types-core'
import { loadKey } from './loadFields'

/** Last-known load for one board, WITH the staleness verdict that went with it. The verdict is cached so a
 *  refresh cannot blink a genuine "this load left the board" warning off while it re-checks. */
export interface SelectedLoadSnapshot {
    load: CanonicalLoad
    stale: boolean
}

/** Snapshot per board id (search tab id, or DRIVER_BIDS_KEY). */
export type SelectedLoadCache = Readonly<Record<string, SelectedLoadSnapshot>>

export interface ResolveSelectedLoadInput {
    /** The load this board has open, or null when nothing is open on it. */
    selectedKey: string | null
    /** Board the answer is for: the active search tab id, or DRIVER_BIDS_KEY. */
    boardKey: string
    /** Rows the operator can actually see — the feed after query / equipment / source refinements. */
    visibleLoads: CanonicalLoad[]
    /** The whole loaded feed for this board, before those refinements. */
    boardLoads: CanonicalLoad[]
    /** False for the render between "active tab changed" and "that tab's rows are mounted". */
    boardSettled: boolean
    /** A search is in flight, so an empty board proves nothing yet. */
    boardLoading: boolean
    previousCache: SelectedLoadCache
}

interface ResolveSelectedLoadResult {
    /** What the drawer should show, or null to close it. */
    load: CanonicalLoad | null
    /** Show the "may be booked or expired — re-verify before bidding" banner. */
    stale: boolean
    /** Cache to keep for the next resolve. Never the same object as `previousCache`. */
    cache: SelectedLoadCache
}

/**
 * Decides what the load drawer shows for ONE board, and whether to warn that the load may be dead.
 *
 * A quiet refresh REPLACES the board with a fresh page 1, so a still-open load pushed past page 1 — or
 * briefly absent while the new page lands — would make a plain `.find()` return null and slam the drawer
 * shut mid-read. Hence the per-board snapshot. But the snapshot must NOT paper over the operator's own
 * filters: `visibleLoads` is also narrowed by the query / equipment / hidden-source toggles.
 *
 * So, in order:
 *   - nothing open            → close, and forget this board's snapshot
 *   - board not settled yet   → answer from the snapshot; the rows on screen belong to another tab and
 *                               cannot say whether THIS board's load is still live
 *   - visible row             → that row, and refresh the snapshot
 *   - filtered out (in feed)  → close, and forget the snapshot: the operator narrowed it away
 *   - gone from the feed      → the snapshot, flagged stale (paginated off, or booked/expired — the two
 *                               are indistinguishable client-side, so warn rather than present it as current)
 *
 * `stale` additionally requires a settled, non-loading board: an in-flight search against an empty board
 * would otherwise accuse a load that is perfectly fine.
 *
 * Pure: `previousCache` is never mutated, so a discarded or double-invoked render cannot leave the cache
 * describing a state the UI never showed.
 */
export const resolveSelectedLoad = ({
    selectedKey,
    boardKey,
    visibleLoads,
    boardLoads,
    boardSettled,
    boardLoading,
    previousCache,
}: ResolveSelectedLoadInput): ResolveSelectedLoadResult => {
    const withoutBoard = (): SelectedLoadCache => {
        if (!(boardKey in previousCache)) return previousCache
        const next = { ...previousCache }
        delete next[boardKey]
        return next
    }

    if (selectedKey == null) return { load: null, stale: false, cache: withoutBoard() }

    const snapshot = previousCache[boardKey] ?? null
    // Mid-switch: the rows on screen belong to the tab being left, so they cannot say whether THIS board's
    // load is still live. Hold the last answer — verdict included.
    if (!boardSettled) return { load: snapshot?.load ?? null, stale: snapshot?.stale ?? false, cache: previousCache }

    const found = visibleLoads.find((l) => loadKey(l) === selectedKey) ?? null
    if (found) return { load: found, stale: false, cache: { ...previousCache, [boardKey]: { load: found, stale: false } } }

    // Still in the feed, just not in the visible rows → the operator filtered it out themselves.
    if (boardLoads.some((l) => loadKey(l) === selectedKey)) return { load: null, stale: false, cache: withoutBoard() }

    if (!snapshot) return { load: null, stale: false, cache: previousCache }

    // Gone from the whole feed. While a fetch is in flight the board cannot prove that either way, so KEEP the
    // standing verdict rather than clearing it: a quiet refresh must not hide a warning that was already earned,
    // and an in-flight first search must not invent one.
    const stale = boardLoading ? snapshot.stale : true
    if (stale === snapshot.stale) return { load: snapshot.load, stale, cache: previousCache }
    return { load: snapshot.load, stale, cache: { ...previousCache, [boardKey]: { load: snapshot.load, stale } } }
}

/**
 * Drop one board's entry — a closed tab must not keep its state alive for the page's lifetime.
 *
 * Generic over the value because EVERY per-board record is keyed the same way and forgets a board the same
 * way: this snapshot cache, the refinements, the hidden chips, the find term, the picked drivers. One helper
 * is what stops the close handlers quietly disagreeing about what closing a tab forgets.
 */
export const omitBoard = <T>(record: Record<string, T>, boardKey: string): Record<string, T> => {
    if (!(boardKey in record)) return record
    const next = { ...record }
    delete next[boardKey]
    return next
}

/** Drop several boards at once (Close all tabs). Boards not in the list survive — notably Driver Bids, which
 *  is reserved rather than a tab, so it is never one of the tabs being closed. */
export const omitBoards = <T>(record: Record<string, T>, boardKeys: string[]): Record<string, T> => {
    const doomed = boardKeys.filter((key) => key in record)
    if (doomed.length === 0) return record
    const next = { ...record }
    for (const key of doomed) delete next[key]
    return next
}
